import React from "react";

import async from "./components/Async";

// All pages that rely on 3rd party components (other than MUI) are
// loaded asynchronously, to keep the initial JS bundle to a minimum size

// Layouts
import AuthLayout from "./layouts/Auth";
import DashboardLayout from "./layouts/Dashboard";

// Guards
import AuthGuard from "./components/guards/AuthGuard";

// Auth components
import SignIn from "./pages/auth/SignIn";
import SignUp from "./pages/auth/SignUp";
import ResetPassword from "./pages/auth/ResetPassword";
import Page401 from "./pages/auth/Page401";
import Page404 from "./pages/auth/Page404";
import Page500 from "./pages/auth/Page500";

// Form components
import Level10Meeting from "./pages/forms/Meeting";

// Page components
import InvoiceDetails from "./pages/pages/InvoiceDetails";
import InvoiceList from "./pages/pages/InvoiceList";
import Orders from "./pages/pages/Orders";
import Pricing from "./pages/pages/Pricing";
import Settings from "./pages/pages/Settings";
import Projects from "./pages/pages/Projects";
import Chat from "./pages/pages/Chat";
import Maintenance from "./pages/pages/Maintenance";

//Tools
import PipeCalculator from "./components/PipeCalculator";

// Protected routes
import ProtectedPage from "./pages/protected/ProtectedPage";

//Logs
const Mpi = async(() => import("./pages/logs/Mpi"));
const MpiFluids = async(() => import("./pages/logs/MpiFluids"));
const Hydro = async(() => import("./pages/logs/Hydro"));
const Utt = async(() => import("./pages/logs/Utt"));
const Nonconformance = async(() => import("./pages/logs/Nonconformance"));
const Slings = async(() => import("./pages/logs/Slings"));
const Kanban = async(() => import("./pages/logs/Kanban"));
const Rebuild = async(() => import("./pages/logs/Rebuild"));
const RebuildScan = async(() => import("./pages/logs/RebuildScan"));

// Tools
const CocWorksheet = async(() => import("./pages/tools/CocWorksheet"));

// Dashboard components
const Default = async(() => import("./pages/dashboards/Default"));

// Department components
const UttDepartment = async(() => import("./pages/departments/Utt"));
const MinWallCatalog = async(() => import("./pages/departments/MinWall"));

// Form components
const RebuildFormBuilder = async(() =>
  import("./pages/forms/rebuild/FormBuilder")
);
const RebuildFormRunner = async(() =>
  import("./pages/forms/rebuild/FormRunner")
);

const DailyForklift = async(() => import("./pages/forms/shop/DailyForklift"));
const DailyCrane = async(() => import("./pages/forms/shop/DailyCrane"));
const FuelLog = async(() => import("./pages/forms/shop/FuelLog"));
const DieselTrashPump = async(() =>
  import("./pages/forms/maintenance/DieselTrashPump")
);

// Page components
const Profile = async(() => import("./pages/pages/Profile"));
const Tasks = async(() => import("./pages/pages/Tasks"));
const Calendar = async(() => import("./pages/pages/Calendar"));
const Users = async(() => import("./pages/pages/Users"));

const routes = [
  {
    path: "/",
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: "",
        element: <Default />,
      },
    ],
  },
  {
    path: "tools",
    element: <DashboardLayout />,
    children: [
      {
        path: "wallCalculator",
        element: <PipeCalculator />,
      },
      {
        // Reads the inventory, hydro, UTT, MPI and rebuild logs, so it
        // needs a signed-in user even though the calculator doesn't.
        path: "coc",
        element: (
          <AuthGuard>
            <CocWorksheet />
          </AuthGuard>
        ),
      },
    ],
  },
  {
    path: "dashboard",
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: "default",
        element: <Default />,
      },
    ],
  },
  {
    path: "pages",
    element: <DashboardLayout />,
    children: [
      {
        path: "profile",
        element: <Profile />,
      },
      {
        path: "settings",
        element: <Settings />,
      },
      {
        path: "pricing",
        element: <Pricing />,
      },
      {
        path: "chat",
        element: <Chat />,
      },
    ],
  },
  {
    path: "departments",
    element: <DashboardLayout />,
    children: [
      {
        path: "maintenance",
        element: <Maintenance />,
      },
      {
        path: "utt",
        element: (
          <AuthGuard>
            <UttDepartment />
          </AuthGuard>
        ),
      },
      {
        path: "min-wall",
        element: (
          <AuthGuard allowed={["ADMIN"]}>
            <MinWallCatalog />
          </AuthGuard>
        ),
      },
    ],
  },
  {
    path: "projects",
    element: <DashboardLayout />,
    children: [
      {
        path: "",
        element: <Projects />,
      },
    ],
  },
  {
    path: "invoices",
    element: <DashboardLayout />,
    children: [
      {
        path: "",
        element: <InvoiceList />,
      },
      {
        path: "detail",
        element: <InvoiceDetails />,
      },
    ],
  },
  {
    path: "orders",
    element: <DashboardLayout />,
    children: [
      {
        path: "",
        element: <Orders />,
      },
    ],
  },
  {
    path: "tasks",
    element: <DashboardLayout />,
    children: [
      {
        path: "",
        element: <Tasks />,
      },
    ],
  },
  {
    path: "calendar",
    element: <DashboardLayout />,
    children: [
      {
        path: "",
        element: <Calendar />,
      },
    ],
  },
  {
    path: "users",
    element: (
      <AuthGuard allowed={["ADMIN"]}>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: "",
        element: <Users />,
      },
    ],
  },
  {
    path: "logs",
    element: <DashboardLayout />,
    children: [
      {
        path: "mpi",
        element: <Mpi />,
      },
      {
        path: "mpi/fluids",
        element: <MpiFluids />,
      },
      {
        path: "hydro",
        element: <Hydro />,
      },
      {
        path: "utt",
        element: <Utt />,
      },
      {
        path: "nonconformance",
        element: <Nonconformance />,
      },
      {
        path: "slings",
        element: <Slings />,
      },
      {
        path: "kanban",
        element: <Kanban />,
      },
      {
        path: "rebuild",
        element: <Rebuild />,
      },
      {
        // Static segments outrank `:recordId`, so these match first.
        path: "rebuild/scan",
        element: (
          <AuthGuard allowed={["ADMIN", "QHSE Manager", "COORDINATOR"]}>
            <RebuildScan />
          </AuthGuard>
        ),
      },
      {
        path: "rebuild/scan/:batchId",
        element: (
          <AuthGuard allowed={["ADMIN", "QHSE Manager", "COORDINATOR"]}>
            <RebuildScan />
          </AuthGuard>
        ),
      },
      {
        path: "rebuild/:recordId",
        element: (
          <AuthGuard>
            <RebuildFormRunner />
          </AuthGuard>
        ),
      },
    ],
  },
  {
    path: "auth",
    element: <AuthLayout />,
    children: [
      {
        path: "sign-in",
        element: <SignIn />,
      },
      {
        path: "sign-up",
        element: <SignUp />,
      },
      {
        path: "reset-password",
        element: <ResetPassword />,
      },
      {
        path: "401",
        element: <Page401 />,
      },
      {
        path: "404",
        element: <Page404 />,
      },
      {
        path: "500",
        element: <Page500 />,
      },
    ],
  },
  {
    path: "forms",
    element: <DashboardLayout />,
    children: [
      {
        path: "level10",
        element: <Level10Meeting />,
      },
      {
        path: "fuelLog",
        element: (
          <AuthGuard>
            <FuelLog />
          </AuthGuard>
        ),
      },
      {
        path: "dailyForklift/:recordId",
        element: (
          <AuthGuard>
            <DailyForklift />
          </AuthGuard>
        ),
      },
      {
        path: "dailyForklift",
        element: (
          <AuthGuard>
            <DailyForklift />
          </AuthGuard>
        ),
      },
      {
        path: "dailyCrane/:recordId",
        element: (
          <AuthGuard>
            <DailyCrane />
          </AuthGuard>
        ),
      },
      {
        path: "dailyCrane",
        element: (
          <AuthGuard>
            <DailyCrane />
          </AuthGuard>
        ),
      },
      {
        path: "dieselTrashPump/:recordId",
        element: (
          <AuthGuard>
            <DieselTrashPump />
          </AuthGuard>
        ),
      },
      {
        path: "dieselTrashPump",
        element: (
          <AuthGuard>
            <DieselTrashPump />
          </AuthGuard>
        ),
      },
      {
        path: "rebuild/builder",
        element: (
          <AuthGuard>
            <RebuildFormBuilder />
          </AuthGuard>
        ),
      },

      // Rebuild form builder (edit existing form)
      {
        path: "rebuild/builder/:formId",
        element: (
          <AuthGuard>
            <RebuildFormBuilder />
          </AuthGuard>
        ),
      },

      // Rebuild form runner (use form to add a record)
      {
        path: "rebuild/:formId",
        element: (
          <AuthGuard>
            <RebuildFormRunner />
          </AuthGuard>
        ),
      },
    ],
  },
  {
    path: "private",
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: "",
        element: <ProtectedPage />,
      },
    ],
  },
  {
    path: "adminOnly",
    element: (
      <AuthGuard allowed={["ADMIN"]}>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: "",
        element: <ProtectedPage />,
      },
    ],
  },
  {
    path: "*",
    element: <AuthLayout />,
    children: [
      {
        path: "*",
        element: <Page404 />,
      },
    ],
  },
];

export default routes;
